import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { TagFilterBar } from "../tag-filter-bar/tag-filter-bar";
import { CheckboxList, CheckboxListItem } from "../checkbox-list/checkbox-list";
import { FooterActions } from "../footer-actions/footer-actions";
import "./multi-select-with-tag.scss";

export interface MultiSelectWithTagOption {
  id: string;
  label: string;
  disabled?: boolean;
}

export interface MultiSelectWithTagProps {
  /** All available options to choose from. */
  options: MultiSelectWithTagOption[];
  /** Selected option ids (controlled). When provided, component is controlled. */
  value?: string[];
  /** Initial selected ids when uncontrolled. */
  defaultValue?: string[];
  /** Called when selection changes. Receives the new list of selected ids. */
  onChange?: (selectedIds: string[]) => void;
  /** Label for the "Select All" row in the dropdown. Default "Select All". */
  selectAllLabel?: string;
  /**
   * Whether to show the Select All row in the dropdown. Default true.
   * Hidden while `singleSelect` is true. During async search, shown only when
   * there is at least one result; clicking it selects every visible result
   * without clearing selected tags that are not in the current result set.
   */
  showSelectAll?: boolean;
  /** Whether to show the remove (X) icon on each selected tag. Default true. */
  showTagRemoveIcon?: boolean;
  /** Placeholder when no tags are selected. Shown in the bar area. @default "Select..." */
  placeholder?: string;
  /**
   * Async search callback. When provided, a search input appears above "Select All".
   * Called after debounce when the user types >= searchMinChars characters.
   * Must return the options to display in the dropdown. When the query is empty
   * (search cleared), the original `options` prop is shown automatically.
   */
  onSearch?: (query: string) => Promise<MultiSelectWithTagOption[]>;
  /** Placeholder for the search input. @default "Search..." */
  searchPlaceholder?: string;
  /** Minimum characters required before triggering onSearch. @default 2 */
  searchMinChars?: number;
  /** Debounce delay in milliseconds for the search input. @default 300 */
  searchDebounceMs?: number;
  /** Message shown when search returns no results. @default "No results found" */
  searchNoResultsMessage?: string;
  /**
   * When true and `onSearch` is provided, pressing Enter after a completed search with no
   * exact match in the results adds the search text as a new tag.
   * @default true
   */
  allowCreateOnSearchEnter?: boolean;
  /**
   * Optional formatter for the option created when the user presses Enter on an unmatched search.
   * When omitted, id is derived from the label and label is the trimmed search query.
   */
  onCreateOptionFromSearch?: (query: string) => MultiSelectWithTagOption;
  /**
   * Hint shown when create-on-enter is available. Use `{query}` for the search text.
   * @default 'Press Enter to add "{query}"'
   */
  searchCreateOnEnterHint?: string;
  /** Width of the bar (e.g. "100%", "35rem"). When dynamicWidth is true, this is max-width and bar uses fit-content. */
  barWidth?: string | number;
  /** Minimum width (rem or string) of the bar when using dynamic width. Default 12rem. */
  barMinWidth?: string | number;
  /** Width of the dropdown list panel (e.g. "17.5rem"). When dynamic, this is min-width; content can grow. */
  dropdownWidth?: string | number;
  /** Minimum width (rem or string) of the dropdown when using dynamic width. Default 12rem. */
  dropdownMinWidth?: string | number;
  /** When true (default), bar and dropdown sizes follow content with min/max. When false, bar and dropdown use fixed widths. */
  dynamicWidth?: boolean;
  /** Max height of the dropdown (e.g. "20rem") for scrolling when there are many options. */
  dropdownMaxHeight?: string | number;
  /** Open state when controlled. */
  open?: boolean;
  /** Called when dropdown open state changes. */
  onOpenChange?: (open: boolean) => void;
  /** Bar: tag background color. */
  tagBackgroundColor?: string;
  /** Bar: tag text/border color. */
  tagTextColor?: string;
  /** Bar: tag border color. */
  tagBorderColor?: string;
  /** Dropdown: checkbox/list styling (passed to CheckboxList). All numeric values in rem. */
  checkboxListProps?: Partial<{
    rowPaddingY: number;
    checkboxLabelGap: number;
    paddingX: number;
    paddingY: number;
    labelColor: string;
    checkboxBorderColor: string;
    checkboxCheckedColor: string;
    checkmarkColor: string;
    selectAllLabelFontWeight: "bold" | "regular";
  }>;
  /** Dropdown button aria-label. Default "Add or select filters". */
  dropdownAriaLabel?: string;
  /** Root className. */
  className?: string;
  /** Root style. */
  style?: React.CSSProperties;
  /** Disabled: bar and dropdown are disabled. */
  disabled?: boolean;
  /**
   * When true, hides the Apply/Cancel footer below the options. When false (default), the footer is shown.
   */
  hideFooter?: boolean;
  /** Primary button label in the dropdown footer. @default "Apply" */
  footerPrimaryLabel?: string;
  /** Secondary button label in the dropdown footer. @default "Cancel" */
  footerSecondaryLabel?: string;
  /** Called when Apply is clicked. Receives current selected ids. If not provided, only closes dropdown. */
  onFooterApply?: (selectedIds: string[]) => void;
  /** Called when Cancel is clicked. If not provided, selection reverts to state at open and dropdown closes. */
  onFooterCancel?: () => void;
  /**
   * When true, tags stay on a single horizontal line and the bar becomes horizontally scrollable.
   * When false (default), tags wrap to the next line.
   */
  horizontalScrollTags?: boolean;
  /**
   * When true, only one option can be selected at a time.
   * Also forces the "Select All" row to be hidden.
   * @default false
   */
  singleSelect?: boolean;
  /**
   * Portal mount for the dropdown. Coordinates are computed relative to this
   * container (`position: absolute`). The container should have `position: relative`.
   * When omitted, the dropdown portals to `document.body` with `position: fixed`.
   */
  targetRef?: React.RefObject<HTMLElement | null>;
}

export const MultiSelectWithTag: React.FC<MultiSelectWithTagProps> = ({
  options,
  value: controlledValue,
  defaultValue = [],
  onChange,
  selectAllLabel = "Select All",
  showSelectAll = true,
  placeholder,
  barWidth = "100%",
  barMinWidth = "12rem",
  dropdownWidth = "17.5rem",
  dropdownMinWidth = "12rem",
  dynamicWidth = true,
  dropdownMaxHeight = "20rem",
  open: controlledOpen,
  onOpenChange,
  tagBackgroundColor,
  tagTextColor,
  tagBorderColor,
  checkboxListProps = {},
  dropdownAriaLabel = "Add or select filters",
  showTagRemoveIcon = true,
  className = "",
  style,
  disabled = false,
  hideFooter = false,
  footerPrimaryLabel = "Apply",
  footerSecondaryLabel = "Cancel",
  onFooterApply,
  onFooterCancel,
  horizontalScrollTags = false,
  onSearch,
  searchPlaceholder = "Search...",
  searchMinChars = 2,
  searchDebounceMs = 300,
  searchNoResultsMessage = "No results found",
  allowCreateOnSearchEnter = true,
  onCreateOptionFromSearch,
  searchCreateOnEnterHint = 'Press Enter to add "{query}"',
  singleSelect = false,
  targetRef,
}) => {
  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState<string[]>(defaultValue);
  const selectedIds = isControlled ? controlledValue : internalValue;

  const isOpenControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = isOpenControlled ? controlledOpen : internalOpen;

  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  /** When footer is shown and user clicks Cancel, revert to this selection (set when dropdown opened). */
  const selectionAtOpenRef = useRef<string[]>([]);
  const [dropdownMinWidthPx, setDropdownMinWidthPx] = useState<number | null>(null);
  const [panelPosition, setPanelPosition] = useState<{
    position: "fixed" | "absolute";
    top: number;
    left: number;
    width: number;
    dropDirection: "down" | "up";
    bottom?: number;
  } | null>(null);

  const showSearch = !!onSearch;
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<MultiSelectWithTagOption[] | null>(null);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchRequestIdRef = useRef(0);
  /** Accumulates all options ever seen (from props + search results) so tag labels always resolve. */
  const allKnownOptionsRef = useRef<Map<string, MultiSelectWithTagOption>>(new Map());

  for (const opt of options ?? []) {
    allKnownOptionsRef.current.set(opt.id, opt);
  }
  if (Array.isArray(searchResults)) {
    for (const opt of searchResults) {
      allKnownOptionsRef.current.set(opt.id, opt);
    }
  }

  const isActiveSearch = showSearch && Array.isArray(searchResults);
  const displayOptions = isActiveSearch ? searchResults : (options ?? []);
  const trimmedSearchQuery = searchQuery.trim();
  // Idle list: respect `showSelectAll`. Active search: only when there is at
  // least one visible result so Select All means "all currently shown options".
  const showSelectAllInDropdown =
    !singleSelect &&
    showSelectAll &&
    !(isActiveSearch && displayOptions.length === 0);

  const canCreateFromSearch =
    showSearch &&
    allowCreateOnSearchEnter &&
    trimmedSearchQuery.length >= searchMinChars &&
    !isSearchLoading &&
    isActiveSearch &&
    !findExactMatchOption(displayOptions, trimmedSearchQuery);

  const createOnEnterHint =
    canCreateFromSearch
      ? searchCreateOnEnterHint.replace("{query}", trimmedSearchQuery)
      : null;

  const setOpen = useCallback(
    (open: boolean) => {
      if (!open && showSearch) {
        setSearchQuery("");
        setSearchResults(null);
        setIsSearchLoading(false);
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        searchRequestIdRef.current++;
      }
      if (!isOpenControlled) setInternalOpen(open);
      onOpenChange?.(open);
    },
    [isOpenControlled, onOpenChange, showSearch]
  );

  const setSelectedIds = useCallback(
    (ids: string[]) => {
      if (!isControlled) setInternalValue(ids);
      onChange?.(ids);
    },
    [isControlled, onChange]
  );

  const selectedTags = selectedIds
    .map((id) => allKnownOptionsRef.current.get(id))
    .filter((o): o is MultiSelectWithTagOption => o !== undefined);

  const handleTagRemove = useCallback(
    (_label: string, index: number) => {
      const tagToRemove = selectedTags[index];
      if (tagToRemove) {
        setSelectedIds(selectedIds.filter((id) => id !== tagToRemove.id));
      }
    },
    [selectedIds, selectedTags, setSelectedIds]
  );

  const handleDropdownClick = useCallback(() => {
    if (disabled) return;
    setOpen(!isOpen);
  }, [disabled, isOpen, setOpen]);

  const checkboxItems: CheckboxListItem[] = displayOptions.map((opt) => ({
    id: opt.id,
    label: opt.label,
    checked: selectedIds.includes(opt.id),
    disabled: opt.disabled,
  }));

  const handleSelectionChange = useCallback(
    (items: CheckboxListItem[]) => {
      if (singleSelect) {
        const newlyCheckedId = items.find(
          (i) => i.checked && !selectedIds.includes(i.id)
        )?.id;
        if (newlyCheckedId) {
          setSelectedIds([newlyCheckedId]);
        } else {
          const firstStillChecked = items.find((i) => i.checked)?.id;
          setSelectedIds(firstStillChecked ? [firstStillChecked] : []);
        }
        return;
      }
      if (isActiveSearch) {
        const displayedIds = new Set(items.map((i) => i.id));
        const checkedFromDisplay = items.filter((i) => i.checked).map((i) => i.id);
        const preservedFromOutside = selectedIds.filter((id) => !displayedIds.has(id));
        setSelectedIds([...preservedFromOutside, ...checkedFromDisplay]);
      } else {
        const newSelectedIds = items.filter((i) => i.checked).map((i) => i.id);
        setSelectedIds(newSelectedIds);
      }
    },
    [setSelectedIds, isActiveSearch, selectedIds, singleSelect]
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const query = e.target.value;
      setSearchQuery(query);

      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

      if (query.length < searchMinChars) {
        setSearchResults(null);
        setIsSearchLoading(false);
        return;
      }

      setIsSearchLoading(true);
      debounceTimerRef.current = setTimeout(async () => {
        const requestId = ++searchRequestIdRef.current;
        try {
          const results = await onSearch!(query);
          if (requestId === searchRequestIdRef.current) {
            setSearchResults(Array.isArray(results) ? results : []);
          }
        } catch {
          if (requestId === searchRequestIdRef.current) {
            setSearchResults([]);
          }
        } finally {
          if (requestId === searchRequestIdRef.current) {
            setIsSearchLoading(false);
          }
        }
      }, searchDebounceMs);
    },
    [onSearch, searchMinChars, searchDebounceMs]
  );

  const clearSearchState = useCallback(() => {
    setSearchQuery("");
    setSearchResults(null);
    setIsSearchLoading(false);
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    searchRequestIdRef.current++;
  }, []);

  const addOptionToSelection = useCallback(
    (option: MultiSelectWithTagOption) => {
      allKnownOptionsRef.current.set(option.id, option);
      if (singleSelect) {
        setSelectedIds([option.id]);
      } else if (!selectedIds.includes(option.id)) {
        setSelectedIds([...selectedIds, option.id]);
      }
    },
    [selectedIds, setSelectedIds, singleSelect]
  );

  const handleCreateFromSearch = useCallback(() => {
    if (!canCreateFromSearch) return;

    const existingByLabel = Array.from(allKnownOptionsRef.current.values()).find(
      (o) => o.label.trim().toLowerCase() === trimmedSearchQuery.toLowerCase()
    );
    if (existingByLabel) {
      addOptionToSelection(existingByLabel);
      clearSearchState();
      searchInputRef.current?.focus();
      return;
    }

    const newOption =
      onCreateOptionFromSearch?.(trimmedSearchQuery) ??
      ({
        id: generateUniqueOptionId(trimmedSearchQuery, allKnownOptionsRef.current),
        label: trimmedSearchQuery,
      } satisfies MultiSelectWithTagOption);

    addOptionToSelection(newOption);
    clearSearchState();
    searchInputRef.current?.focus();
  }, [
    addOptionToSelection,
    canCreateFromSearch,
    clearSearchState,
    onCreateOptionFromSearch,
    trimmedSearchQuery,
  ]);

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== "Enter" || trimmedSearchQuery.length < searchMinChars) return;

      const exactMatch = findExactMatchOption(displayOptions, trimmedSearchQuery);
      if (exactMatch) {
        e.preventDefault();
        addOptionToSelection(exactMatch);
        clearSearchState();
        searchInputRef.current?.focus();
        return;
      }

      if (!canCreateFromSearch) return;
      e.preventDefault();
      handleCreateFromSearch();
    },
    [
      addOptionToSelection,
      canCreateFromSearch,
      clearSearchState,
      displayOptions,
      handleCreateFromSearch,
      searchMinChars,
      trimmedSearchQuery,
    ]
  );

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (isOpen && showSearch) {
      requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  }, [isOpen, showSearch]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        const target = event.target as Node;
        if (dropdownRef.current && dropdownRef.current.contains(target)) return;
        setOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, setOpen]);

  // Compute panel position for portal and drop direction.
  // targetRef: absolute coords inside that container; otherwise fixed to the viewport.
  const updatePanelPosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    if (dynamicWidth && barRef.current) {
      setDropdownMinWidthPx(barRef.current.offsetWidth);
    } else {
      setDropdownMinWidthPx(null);
    }
    const width =
      (dynamicWidth && barRef.current ? barRef.current.offsetWidth : rect.width) ?? rect.width;
    const portalRoot = targetRef?.current ?? null;
    let dropDirection: "down" | "up" = "down";
    if (dropdownRef.current) {
      const dropdownHeight = dropdownRef.current.offsetHeight;
      const spaceBelow = portalRoot
        ? portalRoot.getBoundingClientRect().bottom - rect.bottom
        : window.innerHeight - rect.bottom;
      if (spaceBelow < dropdownHeight) {
        dropDirection = "up";
      }
    }
    if (portalRoot) {
      const containerRect = portalRoot.getBoundingClientRect();
      const scrollTop = portalRoot.scrollTop;
      const scrollLeft = portalRoot.scrollLeft;
      setPanelPosition({
        position: "absolute",
        top:
          dropDirection === "down"
            ? rect.bottom - containerRect.top + scrollTop
            : 0,
        left: rect.left - containerRect.left + scrollLeft,
        width,
        dropDirection,
        bottom:
          dropDirection === "up"
            ? containerRect.bottom - rect.top + scrollTop
            : undefined,
      });
      return;
    }
    setPanelPosition({
      position: "fixed",
      top: dropDirection === "down" ? rect.bottom : 0,
      left: rect.left,
      width,
      dropDirection,
      bottom: dropDirection === "up" ? window.innerHeight - rect.top : undefined,
    });
  }, [dynamicWidth, targetRef]);

  // When open: position panel (and re-run after mount so dropdown height is known), subscribe to scroll/resize
  useLayoutEffect(() => {
    if (!isOpen || !containerRef.current) {
      if (!isOpen) setPanelPosition(null);
      return;
    }
    updatePanelPosition();
    const raf = requestAnimationFrame(() => updatePanelPosition());
    window.addEventListener("scroll", updatePanelPosition, true);
    window.addEventListener("resize", updatePanelPosition);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", updatePanelPosition, true);
      window.removeEventListener("resize", updatePanelPosition);
    };
  }, [isOpen, updatePanelPosition]);

  // When dropdown is open and dynamic: keep dropdown width in sync with bar width (ResizeObserver)
  useEffect(() => {
    if (!isOpen || !dynamicWidth || !barRef.current) return;
    const el = barRef.current;
    const syncBarWidth = () => {
      setDropdownMinWidthPx(el.offsetWidth);
      updatePanelPosition();
    };
    const ro = new ResizeObserver(syncBarWidth);
    ro.observe(el);
    syncBarWidth();
    return () => ro.disconnect();
  }, [isOpen, dynamicWidth, selectedTags.length, updatePanelPosition]);

  // When dropdown opens with footer, store current selection for possible Cancel revert
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !hideFooter && !prevOpenRef.current) {
      selectionAtOpenRef.current = [...selectedIds];
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, hideFooter, selectedIds]);

  const handleFooterApply = useCallback(() => {
    setOpen(false);
    onFooterApply?.(selectedIds);
  }, [setOpen, onFooterApply, selectedIds]);

  const handleFooterCancel = useCallback(() => {
    setSelectedIds(selectionAtOpenRef.current);
    setOpen(false);
    onFooterCancel?.();
  }, [setSelectedIds, setOpen, onFooterCancel]);

  const barContent =
    selectedTags.length > 0 ? (
      <TagFilterBar
        tags={selectedTags.map((o) => ({
          id: o.id,
          label: o.label.length > 25 ? o.label.slice(0, 25) + "..." : o.label,
          title: o.label,
        }))}
        onTagRemove={handleTagRemove}
        onDropdownClick={handleDropdownClick}
        showDropdown={!disabled}
        closable={showTagRemoveIcon && !disabled}
        dropdownAriaLabel={dropdownAriaLabel}
        tagBackgroundColor={tagBackgroundColor}
        tagTextColor={tagTextColor}
        tagBorderColor={tagBorderColor}
        singleLineScrollable={horizontalScrollTags}
      />
    ) : (
      <button
        type="button"
        className="multi-select-with-tag__trigger"
        onClick={handleDropdownClick}
        disabled={disabled}
        aria-label={dropdownAriaLabel}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="multi-select-with-tag__placeholder">
          {placeholder ?? "Select..."}
        </span>
        <span className={`multi-select-with-tag__chevron ${isOpen ? "multi-select-with-tag__chevron--open" : ""}`}>
          <ChevronDownIcon />
        </span>
      </button>
    );

  return (
    <div
      ref={containerRef}
      className={`multi-select-with-tag ${className}`}
      style={{ ...(typeof barWidth === "number" ? { width: `${barWidth}rem` } : { width: barWidth }), ...style }}
    >
      <div
        ref={barRef}
        className={`multi-select-with-tag__bar ${horizontalScrollTags ? "multi-select-with-tag__bar--horizontal-scroll" : ""}`}
        style={
          dynamicWidth && !horizontalScrollTags
            ? {
                width: "100%",
                minWidth: typeof barMinWidth === "number" ? `${barMinWidth}rem` : barMinWidth,
                
              }
            : horizontalScrollTags
              ? { width: "100%", minWidth: typeof barMinWidth === "number" ? `${barMinWidth}rem` : barMinWidth }
              : undefined
        }
      >
        {barContent}
      </div>

      {isOpen &&
        createPortal(
          panelPosition ? (
            <div
              ref={dropdownRef}
              className={`multi-select-with-tag__panel multi-select-with-tag__panel--${panelPosition.dropDirection} ${dynamicWidth ? "multi-select-with-tag__panel--dynamic" : ""} ${!hideFooter ? "multi-select-with-tag__panel--with-footer" : ""} ${showSearch ? "multi-select-with-tag__panel--with-search" : ""}`}
              style={{
                position: panelPosition.position,
                left: panelPosition.left,
                ...(panelPosition.dropDirection === "down"
                  ? { top: panelPosition.top }
                  : { bottom: panelPosition.bottom }),
                ...(dynamicWidth
                  ? {
                      width: dropdownMinWidthPx != null ? `${dropdownMinWidthPx}px` : (typeof dropdownMinWidth === "number" ? `${dropdownMinWidth}rem` : dropdownMinWidth),
                      minWidth: dropdownMinWidthPx != null ? `${dropdownMinWidthPx}px` : (typeof dropdownMinWidth === "number" ? `${dropdownMinWidth}rem` : dropdownMinWidth),
                    }
                  : {
                      width: panelPosition.width,
                      minWidth: typeof dropdownWidth === "number" ? `${dropdownWidth}rem` : dropdownWidth,
                    }),
                ...(typeof dropdownMaxHeight === "number" ? { maxHeight: `${dropdownMaxHeight}rem` } : { maxHeight: dropdownMaxHeight }),
                ...((showSearch || !hideFooter)
                  ? {
                      ["--multi-select-inner-max-height" as string]: (() => {
                        const footerOffset = !hideFooter ? 3.5 : 0;
                        const searchOffset = showSearch ? 2.5 : 0;
                        const totalOffset = footerOffset + searchOffset;
                        return typeof dropdownMaxHeight === "number"
                          ? `calc(${dropdownMaxHeight}rem - ${totalOffset}rem)`
                          : `calc(${dropdownMaxHeight} - ${totalOffset}rem)`;
                      })(),
                    }
                  : {}),
              }}
              role="listbox"
              aria-label="Filter options"
            >
              {showSearch && (
                <div className="multi-select-with-tag__search">
                  <SearchIcon />
                  <input
                    ref={searchInputRef}
                    type="text"
                    className="multi-select-with-tag__search-input"
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchKeyDown}
                    aria-label="Search options"
                    autoComplete="off"
                  />
                  {isSearchLoading && (
                    <span className="multi-select-with-tag__search-spinner" aria-label="Loading" />
                  )}
                  {!isSearchLoading && searchQuery.length > 0 && (
                    <button
                      type="button"
                      className="multi-select-with-tag__search-clear"
                      aria-label="Clear search"
                      onClick={() => {
                        setSearchQuery("");
                        setSearchResults(null);
                        setIsSearchLoading(false);
                        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
                        searchRequestIdRef.current++;
                        searchInputRef.current?.focus();
                      }}
                    >
                      <ClearIcon />
                    </button>
                  )}
                </div>
              )}
              <div className="multi-select-with-tag__panel-inner">
                {isSearchLoading && displayOptions.length === 0 ? (
                  <div className="multi-select-with-tag__search-status">
                    <span className="multi-select-with-tag__search-spinner" aria-label="Loading" />
                    <span>Searching...</span>
                  </div>
                ) : isActiveSearch && displayOptions.length === 0 ? (
                  <div className="multi-select-with-tag__search-status">
                    <span>{searchNoResultsMessage}</span>
                    {createOnEnterHint && (
                      <span className="multi-select-with-tag__search-create-hint">
                        {createOnEnterHint}
                      </span>
                    )}
                  </div>
                ) : createOnEnterHint ? (
                  <div
                    className="multi-select-with-tag__search-create-hint multi-select-with-tag__search-create-hint--inline"
                    role="status"
                  >
                    {createOnEnterHint}
                  </div>
                ) : null}
                <CheckboxList
                  items={checkboxItems}
                  onSelectionChange={handleSelectionChange}
                  selectAllLabel={selectAllLabel}
                  showSelectAll={showSelectAllInDropdown}
                  width={dynamicWidth ? "100%" : "100%"}
                  paddingX={checkboxListProps.paddingX ?? 1}
                  paddingY={checkboxListProps.paddingY ?? 0.625}
                  rowPaddingY={checkboxListProps.rowPaddingY ?? 0.625}
                  checkboxLabelGap={checkboxListProps.checkboxLabelGap ?? 0.75}
                  labelColor={checkboxListProps.labelColor}
                  checkboxBorderColor={checkboxListProps.checkboxBorderColor}
                  checkboxCheckedColor={checkboxListProps.checkboxCheckedColor}
                  checkmarkColor={checkboxListProps.checkmarkColor}
                  selectAllLabelFontWeight={checkboxListProps.selectAllLabelFontWeight}
                  ariaLabel="Filter options"
                />
              </div>
              {!hideFooter && (
                <FooterActions
                  primaryAction={{
                    label: footerPrimaryLabel,
                    onClick: handleFooterApply,
                  }}
                  secondaryAction={{
                    label: footerSecondaryLabel,
                    onClick: handleFooterCancel,
                  }}
                  showTopBorder={true}
                  fullWidthButtons={true}
                  className="multi-select-with-tag__footer"
                />
              )}
            </div>
          ) : null,
          targetRef?.current ?? document.body
        )}
    </div>
  );
};

function findExactMatchOption(
  options: MultiSelectWithTagOption[] | null | undefined,
  query: string
): MultiSelectWithTagOption | undefined {
  const normalized = query.trim().toLowerCase();
  if (!normalized || !options?.length) return undefined;
  return options.find((o) => o.label.trim().toLowerCase() === normalized);
}

function slugifyOptionId(label: string): string {
  const slug = label
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-_]/g, "");
  return slug || `custom-${Date.now()}`;
}

function generateUniqueOptionId(
  label: string,
  known: Map<string, MultiSelectWithTagOption>
): string {
  const base = slugifyOptionId(label);
  if (!known.has(base)) return base;
  let suffix = 1;
  while (known.has(`${base}-${suffix}`)) {
    suffix += 1;
  }
  return `${base}-${suffix}`;
}

function ChevronDownIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M4 6L8 10L12 6"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M6.417 11.083a4.667 4.667 0 1 0 0-9.333 4.667 4.667 0 0 0 0 9.333ZM12.25 12.25l-2.534-2.534"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ClearIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M9 3L3 9M3 3l6 6"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
